/**
 * AuthContext.js - SECURITY HARDENED VERSION
 * 
 * SECURITY FIX (Feb 22, 2026):
 * - Removed axios.defaults.headers usage (caused credential leakage between users)
 * - Implemented axios request interceptor for per-request token injection
 * - Each request now dynamically fetches token from localStorage
 * - This ensures complete user isolation and prevents session hijacking
 */

import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { safeLocalStorage } from '../utils/safeStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorIdRef = useRef(null);

  // SECURITY: Setup axios request interceptor for per-request token injection
  useEffect(() => {
    // Remove any existing interceptor first
    if (interceptorIdRef.current !== null) {
      axios.interceptors.request.eject(interceptorIdRef.current);
    }

    // Add new request interceptor
    interceptorIdRef.current = axios.interceptors.request.use(
      (config) => {
        // SECURITY: Always fetch fresh token from localStorage for each request
        // This ensures each user's request uses their own token
        const currentToken = safeLocalStorage.getItem('auth_token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        } else {
          // Remove Authorization header if no token exists
          delete config.headers.Authorization;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, []);

  // Load saved user session on mount
  useEffect(() => {
    const savedUser = safeLocalStorage.getItem('user');
    const savedToken = safeLocalStorage.getItem('auth_token');
    
    if (savedUser && savedToken) {
      try {
        setUser(JSON.parse(savedUser));
        setToken(savedToken);
        // SECURITY: DO NOT use axios.defaults.headers
        // Token is now injected via interceptor per-request
      } catch (e) {
        console.warn('Failed to parse saved user data');
        // Clear invalid data
        safeLocalStorage.removeItem('user');
        safeLocalStorage.removeItem('auth_token');
      }
    }
    setLoading(false);
  }, []);

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password
      });
      
      if (response.data && response.data.user) {
        return { 
          success: true, 
          userId: response.data.user.id,
          user: response.data.user 
        };
      }
      
      return { success: true, userId: response.data.id };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setToken(null);
    
    // Clear localStorage
    safeLocalStorage.removeItem('user');
    safeLocalStorage.removeItem('auth_token');
    
    // SECURITY: DO NOT delete axios.defaults.headers
    // Interceptor will handle missing token automatically
    // This prevents any shared state issues
  };

  const isAdmin = () => {
    return user?.is_admin === true;
  };

  const isAuthenticated = () => {
    return user !== null && token !== null;
  };

  const getToken = () => {
    // SECURITY: Always return fresh token from localStorage
    return safeLocalStorage.getItem('auth_token') || token;
  };

  // SECURITY: Helper to get auth headers for manual fetch calls
  const getAuthHeaders = () => {
    const currentToken = safeLocalStorage.getItem('auth_token');
    return currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      token,
      setToken,
      loading,
      register,
      logout,
      isAdmin,
      isAuthenticated,
      getToken,
      getAuthHeaders
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
